import type { DvrActions } from '@mimir/primitive/dvr/dvrActions';
import type { ICepBridgeClient } from '@mimir/primitive/premiere/cepBridgeClientType';
import { logErrorAndShowToast } from '../util/uiError';
import type { ElectronContext } from '@mimir/common/context/applicationContext';
import { invariant } from '@mimir/common/util/invariant';
import { getGlobalStore } from '../store';

export class DvrClient implements ICepBridgeClient {
  private context: ElectronContext | null = null;
  async call<A extends keyof DvrActions>(action: A, ...args: Parameters<DvrActions[A]>): Promise<ReturnType<DvrActions[A]>> {
    invariant(this.context, 'Must set ElectronContext before using DvrClient.call()');
    return await callDvrFunction(
      this.context,
      async () => {
        const script = `return await window.mimirElectron.mainRequire('./dvr').callDvrAction('${action}',${JSON.stringify(args)})`;
        const reply = await Object.getPrototypeOf(async function() {}).constructor(script)();
        return reply;
      }
    );
  }

  setElectronContext(context: ElectronContext) {
    this.context = context;
  }
}

const callDvrFunction = async<T> (context: ElectronContext, f: () => Promise<T>): Promise<T> => {
  try {
    return await f();
  } catch (err) {
    const stringifiedError = `${err}`;
    if (stringifiedError.includes('Failed to initialize communication with host')
      || stringifiedError.includes('Failed to communicate with host')) {
      await getGlobalStore().dispatch('handleBrokenDvrConnection', context);
      throw err;
    }

    logErrorAndShowToast(`Could not communicate with DaVinci Resolve: ${stringifiedError}`, err, undefined, true);
    throw err;
  }
};
